import React, { useState, FC, PropsWithChildren, ReactNode, useEffect, useCallback } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { FormInputSelect } from 'components/form';
import { Box, Tabs, Tab, TabsProps } from '@mui/material';
import { useLocation, useHistory } from 'react-router-dom';
import { ContentViewType, SelectOption, TabsOption } from 'global';

interface Props<TabsValue extends string> {
  tabProps?: TabsProps;
  initialTab?: TabsValue;
  forceTabValue?: TabsValue;
  tabs: TabsOption<TabsValue>[];
  isSyncWithAnchor?: boolean;
  contentViewType?: ContentViewType;
  TabsLabelWrapper?: FC<PropsWithChildren>;
  onTabChange?: (nextTabValue: TabsValue) => void;
}

export const TabsContainer = <TabsValue extends string = string>({
  tabs,
  tabProps,
  initialTab,
  isSyncWithAnchor = false,
  onTabChange,
  forceTabValue,
  TabsLabelWrapper,
  contentViewType = ContentViewType.Desktop,
}: Props<TabsValue>) => {
  const classes = useStyles();

  const location = useLocation();
  const { replace } = useHistory();

  const [currentTab, setCurrentTab] = useState<TabsValue>(initialTab ?? tabs?.[0]?.value);

  const setAnchor = useCallback(
    (value: TabsValue) => replace({ pathname: location.pathname, search: location.search, hash: `#${value}` }),
    [location.pathname, location.search, replace]
  );

  useEffect(() => {
    if (isSyncWithAnchor) {
      if (!location.hash) {
        setAnchor(currentTab);
      }
      const tab = tabs.find(t => t.urlAnchor === location.hash);
      if (tab) setCurrentTab(tab.value);
    }
  }, [currentTab, isSyncWithAnchor, location, setAnchor, tabs]);

  const handleChangeTab = useCallback(
    (nextTabValue: TabsValue) => {
      if (isSyncWithAnchor) {
        setAnchor(nextTabValue);
      }

      setCurrentTab(nextTabValue);

      if (onTabChange) onTabChange(nextTabValue);
    },
    [isSyncWithAnchor, onTabChange, setAnchor]
  );

  const tabsOptions = tabs
    .filter(({ isVisible = true }) => isVisible)
    .map(({ label, value }) => ({ label: label.toUpperCase(), value })) as SelectOption<TabsValue>[];

  useEffect(() => {
    if (forceTabValue) handleChangeTab(forceTabValue);
  }, [forceTabValue, handleChangeTab]);

  const tabsComponentByViewType: Record<ContentViewType, ReactNode> = {
    [ContentViewType.Desktop]: (
      <Tabs
        variant="scrollable"
        value={currentTab}
        indicatorColor="primary"
        onChange={(_, nextTabValue) => handleChangeTab(nextTabValue)}
        {...tabProps}
      >
        {tabs
          .filter(({ isVisible = true }) => isVisible)
          .map(({ label, value, disabled = false }) => (
            <Tab key={label} label={label} value={value} disabled={disabled} />
          ))}
      </Tabs>
    ),
    [ContentViewType.Mobile]: (
      <FormInputSelect<TabsValue> value={currentTab} options={tabsOptions} onChange={handleChangeTab} />
    ),
  };

  return (
    <>
      <Box className={classes.tabsWrapper}>
        {TabsLabelWrapper ? (
          <TabsLabelWrapper>{tabsComponentByViewType[contentViewType]}</TabsLabelWrapper>
        ) : (
          tabsComponentByViewType[contentViewType]
        )}
      </Box>

      <Box>
        {tabs
          .filter(({ isVisible = true }) => isVisible)
          .map(({ value, component }) => (
            <Box key={value}>{value === currentTab && <>{component}</>}</Box>
          ))}
      </Box>
    </>
  );
};

const useStyles = makeStyles({
  tabsWrapper: {
    borderBottom: '1px solid lightgrey',
  },
});
